import * as React from 'react'
import * as constants from 'static/constants'
import SvgIcon from 'components/atoms/SvgIcon'
import { IWindow } from 'static/interfaces'
import { Tooltip } from 'components/atoms/Tooltip'
import { useWeeklyContext } from 'core/contexts/weekly/WeeklyContext'
import { agentService } from 'core/services'
import styled from 'styled-components'
import { IBuildedTour } from 'utils/weeklyTypes'
import { TourMemoModalBody } from './TourMemoModalBody'
import moment from 'moment'
import Jobs from './Jobs'
import LinkableText from 'components/atoms/LinkableText'

declare var window: IWindow

interface IProps {
  tour: IBuildedTour
  refetchWeeklyTour: (tour_id: number) => void
  handleClose: () => void
}

export const TourDrawerHeader = React.memo(({ tour, refetchWeeklyTour, handleClose }: IProps) => {
  const { onTourUpdate } = useWeeklyContext()
  const tourTitle = `${tour.post.short_name ?? tour.post.name} (${tour.post.duration}h) `
  const tourMemoTemplates = tour.tourMemoTemplates || []
  const tourDateAll = `${moment(tour.day).format('YYYY-MM-DD(dd)')} ${tour.start_time}`
  const excluded = !!tour.excluded_from_payment
  const [updatingExclusion, setUpdatingExclusion] = React.useState(false)

  const handleTogglePaymentExclusion = React.useCallback(async () => {
    if (updatingExclusion) {
      return
    }
    const message = excluded
      ? 'このツアーを支払い除外から戻します。既存の支払明細が再計算されます。よろしいですか？'
      : 'このツアーをガイド支払いの対象から除外します。よろしいですか？'
    if (!window.confirm(message)) {
      return
    }
    setUpdatingExclusion(true)
    try {
      const { tour: updatedTour, flush } = await agentService.updateTourPaymentExclusion(tour.id, {
        excluded_from_payment: !excluded,
      })
      onTourUpdate(updatedTour)
      if (flush) {
        window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      }
    } catch (error) {
      console.error('Failed to update payment exclusion:', error)
    } finally {
      setUpdatingExclusion(false)
    }
  }, [updatingExclusion, excluded, tour.id, onTourUpdate])

  const isCustomMemo = tour.memo_type === 'custom'

  return (
    <Wrapper>
      <div className="inner">
        <div>
          <span className="info">
            <span>{tourDateAll}</span>
            <span>
              MAX
              <b>{`${tour.maxNumber}`}</b>
            </span>
            <span>
              予約済ゲスト
              <b>{`${tour.reservedNumber}`}</b>
            </span>
            <span>
              ツアーグループ
              <b>{`${tour.tour_groups.length}`}</b>
            </span>
          </span>
          <div className="nameContainer">
            <span className="name">
              {tour.post.bokun_activity_id && `[${tour.post.bokun_activity_id}] `}
              {tourTitle}
            </span>
            <Tooltip content="ツアー詳細を編集">
              <a
                href={`/agent/posts/${tour.post.id}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                className="editLink"
              >
                <SvgIcon
                  name="chevron_right"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
              </a>
            </Tooltip>
          </div>
        </div>
        <div className="actions">
          <div
            className={`paymentExclusion${excluded ? ' active' : ''}`}
            onClick={handleTogglePaymentExclusion}
            role="button"
            aria-pressed={excluded}
            title={
              excluded
                ? 'このツアーはガイド支払いの対象外です。クリックで対象に戻します'
                : 'このツアーをガイド支払いの対象から除外します'
            }
          >
            <SvgIcon name="payment" className="icon" ariaHidden={true} role="presentation" />
            <span>{excluded ? '支払い除外中' : '支払い除外'}</span>
          </div>
          <div
            className="reloadDrawer"
            onClick={() => refetchWeeklyTour(tour.id)}
            role="button"
            title="ガイド・ゲスト・ガイド到着など、このツアーの情報を最新の状態に更新します"
          >
            <SvgIcon name="refresh" className="icon" ariaHidden={true} role="presentation" />
            <span>情報更新</span>
          </div>
          <span className="close" onClick={handleClose} role="button" aria-label="Close Drawer">
            <SvgIcon name="close" className="icon" ariaHidden={true} role="presentation" />
          </span>
        </div>
      </div>
      {tour.id && (
        <div className="inner innerBottom">
          <Jobs tour={tour} />
          <div
            className={`inner innerMemo${tour.memo ? ' hasMemo' : ''}${
              isCustomMemo ? ' isCustomMemo' : ''
            }`}
          >
            {tour.memo && (
              <MemoText isCustom={isCustomMemo}>
                <LinkableText>{tour.memo}</LinkableText>
              </MemoText>
            )}
            <MemoButton
              type="button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                window.globalModal.showModal({
                  small: true,
                  body: (
                    <TourMemoModalBody
                      tour={tour}
                      tourMemoTemplates={tourMemoTemplates}
                      onTourUpdate={onTourUpdate}
                    />
                  ),
                  handleSubmit: undefined,
                  zIndex: 6001,
                })
              }}
            >
              <SvgIcon
                name={tour.memo ? 'edit' : 'note'}
                className="icon"
                ariaHidden={true}
                role="presentation"
              />
              {tour.memo ? '編集' : 'ツアーメモを作成する'}
            </MemoButton>
          </div>
        </div>
      )}
    </Wrapper>
  )
})

const Wrapper = styled.div`
  flex: 0 0 auto;
  border-bottom: 1px solid ${constants.COLORS.divider};
  padding: 12px 16px;
  .inner {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    &.innerBottom {
      margin-top: 4px;
      gap: 8px;
      flex-direction: column;
    }
  }
  .innerMemo {
    gap: 8px;
    font-size: 12px;
    overflow: hidden;
    &.hasMemo {
      width: 100%;
      align-items: center;
      justify-content: space-between;
      padding: 4px 8px;
      border-radius: 4px;
      background-color: ${constants.COLORS.background2};
      &.isCustomMemo {
        background-color: ${constants.COLORS.infoBG3};
      }
      .icon {
        width: 16px;
        height: 16px;
      }
    }
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 20px;
    .paymentExclusion {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 32px;
      gap: 4px;
      padding: 0 12px;
      border: 1px solid ${constants.COLORS.divider};
      border-radius: 16px;
      color: ${constants.COLORS.subText};
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      transition: background-color 0.2s, color 0.2s, border-color 0.2s;
      outline: none;
      &:focus,
      &:focus-visible {
        outline: none;
      }
      .icon {
        width: 18px;
        height: 18px;
      }
      &:hover {
        background-color: ${constants.COLORS.cautionBG};
        border-color: ${constants.COLORS.caution};
        color: ${constants.COLORS.cautionText};
      }
      &.active {
        background-color: ${constants.COLORS.cautionBG};
        border-color: ${constants.COLORS.caution};
        color: ${constants.COLORS.cautionText};
        &:hover {
          background-color: ${constants.COLORS.white};
        }
      }
    }
    .reloadDrawer {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 150px;
      height: 32px;
      gap: 4px;

      background: ${constants.COLORS.info2};
      border-radius: 16px;
      color: ${constants.COLORS.white};
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    }
    .close {
      width: 32px;
      height: 32px;
      padding: 4px;
      border-radius: 50%;
      margin: -4px 0;
      cursor: pointer;
      &:hover {
        background-color: ${constants.COLORS.cardBG};
      }

      @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
        top: 12px;
        left: 12px;
      }
    }
  }
  .info {
    color: ${constants.COLORS.subText};
    font-size: 14px;
    span {
      margin-left: 12px;
      &:first-child {
        color: ${constants.COLORS.black};
        margin-left: 0;
      }
      b {
        margin-left: 4px;
      }
    }
  }
  .nameContainer {
    display: flex;
    align-items: center;
    gap: 8px;
    .editLink {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      text-decoration: none;
      color: ${constants.COLORS.subText};
      transition: background-color 0.2s, color 0.2s;
      &:hover {
        background-color: ${constants.COLORS.cardBG};
        color: ${constants.COLORS.primary};
      }
    }
  }
  .name {
    display: block;
  }
`

const MemoButton = styled.button`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
  line-height: 20px;
  color: ${constants.COLORS.primary};
  font-size: 12px;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  .icon {
    width: 20px;
    height: 20px;
  }
  &:hover {
    color: ${constants.COLORS.secondary};
  }
`

const MemoText = styled.span<{ isCustom: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ isCustom }) =>
    isCustom &&
    `
    border-left: 3px solid ${constants.COLORS.info4};
    padding: 2px 6px;
    border-radius: 0 4px 4px 0;
  `}
`
